import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AdminLayout from '../components/AdminLayout'
import { 
  ClipboardList, X, CheckCircle, AlertCircle, Loader, 
  Search, Filter, ChevronLeft, ChevronRight, Calendar, 
  MapPin, Truck, User, Weight, Clock, Eye, Check, 
  XCircle, RefreshCw, Grid, Table, CalendarDays, Zap,
  AlertTriangle, TrendingUp, Users, Car, DollarSign
} from 'lucide-react'
import {
  FetchBookingRequests,
  AdminFindNearestDriver,
  AdminAcceptBooking,
  AdminCancelBooking,
  ResetAdminBookingRequest,
} from '../actions/AdminBookingRequestAction'

// ─── Stat Card Component ─────────────────────────────────────────────────────
function StatCard({ title, value, icon: Icon, color, subtitle }) {
  const colorClasses = {
    blue: { bg: "bg-blue-50", iconBg: "bg-blue-100", text: "text-blue-600", border: "border-blue-200" },
    purple: { bg: "bg-purple-50", iconBg: "bg-purple-100", text: "text-purple-600", border: "border-purple-200" },
    indigo: { bg: "bg-indigo-50", iconBg: "bg-indigo-100", text: "text-indigo-600", border: "border-indigo-200" },
    yellow: { bg: "bg-yellow-50", iconBg: "bg-yellow-100", text: "text-yellow-600", border: "border-yellow-200" },
    green: { bg: "bg-emerald-50", iconBg: "bg-emerald-100", text: "text-emerald-600", border: "border-emerald-200" },
    red: { bg: "bg-red-50", iconBg: "bg-red-100", text: "text-red-600", border: "border-red-200" },
  };
  
  const colors = colorClasses[color] || colorClasses.blue;
  
  return (
    <div className={`${colors.bg} border ${colors.border} rounded-xl p-4 transition-all hover:shadow-md`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`${colors.iconBg} p-3 rounded-xl`}>
          <Icon size={20} className={colors.text} />
        </div>
      </div>
    </div>
  )
}

// ─── Tab Button Component ────────────────────────────────────────────────────
function TabButton({ active, onClick, children, icon: Icon, count }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-all rounded-lg ${
        active
          ? "bg-white text-gray-900 shadow-sm border border-gray-200"
          : "text-gray-500 hover:text-gray-700 hover:bg-white/50"
      }`}
    >
      {Icon && <Icon size={16} />}
      {children}
      {count !== undefined && (
        <span className={`ml-1.5 px-2 py-0.5 rounded-full text-xs ${
          active ? "bg-gray-100 text-gray-700" : "bg-gray-100 text-gray-500"
        }`}>
          {count}
        </span>
      )}
    </button>
  )
}

// ─── Booking Card Component (Mobile View) ───────────────────────────────────
function BookingCard({ booking, onView, onAccept, onCancel, statusColor, normalizeDate, hasConflict, isBlocked, showActions }) {
  const statusClass = statusColor(booking.status);
  
  return (
    <div className={`bg-white rounded-xl border p-4 hover:shadow-md transition-all ${
      hasConflict 
        ? isBlocked 
          ? 'border-red-200 bg-red-50/30' 
          : 'border-yellow-200 bg-yellow-50/30'
        : 'border-gray-100'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">
              #{booking.reqId || booking.bId}
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
              {booking.status === 'Pending' && <Clock size={10} />}
              {booking.status === 'Active' && <CheckCircle size={10} />}
              {booking.status === 'Completed' && <Check size={10} />}
              {booking.status === 'Cancel' && <XCircle size={10} />}
              {booking.status}
            </span>
            {hasConflict && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                isBlocked ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                <AlertTriangle size={10} />
                {isBlocked ? 'Blocked' : 'Conflict'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <User size={12} />
            <span>{booking.bookingSno}</span>
            <span>•</span>
            <Calendar size={12} />
            <span>{normalizeDate(booking.bookingDT)}</span>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3 text-xs mb-3">
        <div>
          <p className="text-gray-500 mb-0.5">Tonnage</p>
          <p className="font-semibold text-gray-900">{booking.tonnage} tons</p>
        </div>
        <div>
          <p className="text-gray-500 mb-0.5">Booking Type</p>
          <p className="font-semibold text-gray-900">{booking.BookingStatus || '-'}</p>
        </div>
        <div className="col-span-2">
          <p className="text-gray-500 mb-0.5">Route</p>
          <p className="font-semibold text-gray-900 flex items-center gap-1">
            <MapPin size={12} className="text-gray-400" />
            <span className="truncate">{booking.From || '—'} → {booking.TO || '—'}</span>
          </p>
        </div>
        <div className="col-span-2">
          <p className="text-gray-500 mb-0.5">Time Slot</p>
          <p className="font-semibold text-gray-900 text-xs">
            {booking.bsTime ? new Date(booking.bsTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
            {' → '}
            {booking.beTime ? new Date(booking.beTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
          </p>
        </div>
      </div>
      
      <div className="flex gap-2 pt-3 border-t border-gray-100">
        <button
          onClick={() => onView(booking)}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
        >
          <Eye size={14} />
          View Details
        </button>
        {showActions && booking.status === 'Pending' && !booking.accepted_by && (
          <>
            <button
              onClick={() => !isBlocked && onAccept(booking)}
              disabled={isBlocked}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition ${
                isBlocked 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'text-white bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              <CheckCircle size={14} />
              Accept
            </button>
            <button
              onClick={() => onCancel(booking.reqId || booking.bId)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition"
            >
              <XCircle size={14} />
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Helper Component ────────────────────────────────────────────────────────
function InfoField({ label, value, badge }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <label className="text-xs text-gray-500 block mb-1">{label}</label>
      {badge ? (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge}`}>
          {value}
        </span>
      ) : (
        <p className="text-sm font-medium text-gray-900 break-words">{value || '-'}</p>
      )}
    </div>
  )
}

// ─── Helper Functions ────────────────────────────────────────────────────────
const parseBookingTime = (val) => {
  if (!val) return null
  const d = new Date(val)
  return isNaN(d.getTime()) ? null : d
}

const doTimesOverlap = (a, b) => {
  const aStart = parseBookingTime(a.bsTime)
  const aEnd = parseBookingTime(a.beTime)
  const bStart = parseBookingTime(b.bsTime)
  const bEnd = parseBookingTime(b.beTime)
  if (!aStart || !aEnd || !bStart || !bEnd) return false
  return aStart < bEnd && bStart < aEnd
}

const normalizeTonnage = (t) =>
  String(t || '')
    .toLowerCase()
    .replace(/[^0-9.]/g, '')

const getConflictingReqIds = (bookings) => {
  const conflicts = new Set()
  for (let i = 0; i < bookings.length; i++) {
    for (let j = i + 1; j < bookings.length; j++) {
      const a = bookings[i]
      const b = bookings[j]
      const isSameTonnage = normalizeTonnage(a.tonnage) === normalizeTonnage(b.tonnage)
      if (isSameTonnage && doTimesOverlap(a, b)) {
        conflicts.add(String(a.reqId || a.bId))
        conflicts.add(String(b.reqId || b.bId))
      }
    }
  }
  return conflicts
}

// ─── Filter Components ───────────────────────────────────────────────────────
function FilterInput({ placeholder, value, onChange, icon: Icon }) {
  return (
    <div className="relative">
      {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />}
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full ${Icon ? 'pl-9' : 'pl-3'} pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white`}
      />
      {value && (
        <X 
          size={14} 
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-red-500 transition" 
          onClick={() => onChange('')} 
        />
      )}
    </div>
  )
}

function FilterSelect({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white"
    >
      <option value="">All Status</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  )
}

function FilterDate({ value, onChange }) {
  return (
    <div className="relative">
      <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white"
      />
      {value && (
        <X 
          size={14} 
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-red-500 transition" 
          onClick={() => onChange('')} 
        />
      )}
    </div>
  )
}

// ─── Table Row Component ─────────────────────────────────────────────────────
const TableRow = ({ booking, onView, onAccept, onCancel, statusColor, normalizeDate, conflictIds, acceptedReqIds, showActions }) => {
  const hasConflict = conflictIds.has(String(booking.reqId || booking.bId))
  const isBlocked = hasConflict && acceptedReqIds.size > 0 && !acceptedReqIds.has(String(booking.reqId || booking.bId))
  
  return (
    <tr className={`hover:bg-gray-50 transition ${hasConflict ? (isBlocked ? 'bg-red-50/30' : 'bg-yellow-50/30') : ''}`}>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-gray-900">#{booking.reqId || booking.bId}</span>
          {hasConflict && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${isBlocked ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
              <AlertTriangle size={10} />
              {isBlocked ? 'Blocked' : 'Conflict'}
            </span>
          )}
        </div>
       </td>
      <td className="py-3 px-4 text-sm text-gray-600 font-medium">{booking.bookingSno}</td>
      <td className="py-3 px-4">
        <span className="inline-flex px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-700 rounded-lg">{booking.tonnage} tons</span>
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <MapPin size={14} className="text-gray-400 shrink-0" />
          <span className="truncate max-w-[180px]">{booking.From || '—'} → {booking.TO || '—'}</span>
        </div>
      </td>
      <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
        <div className="flex items-center gap-1">
          <Clock size={12} className="text-gray-400" />
          <span>
            {booking.bsTime ? new Date(booking.bsTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
            {' → '}
            {booking.beTime ? new Date(booking.beTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
          </span>
        </div>
      </td>
      <td className="py-3 px-4">
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold ${statusColor(booking.status)}`}>
          {booking.status === 'Pending' && <Clock size={10} />}
          {booking.status === 'Active' && <CheckCircle size={10} />}
          {booking.status === 'Completed' && <Check size={10} />}
          {booking.status === 'Cancel' && <XCircle size={10} />}
          {booking.status}
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-gray-500">
        <span className="inline-flex items-center gap-1">
          {booking.BookingStatus === 'Real-time Booking' ? <Zap size={12} /> : <CalendarDays size={12} />}
          {booking.BookingStatus || '-'}
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-gray-500">{normalizeDate(booking.bookingDT)}</td>
      <td className="py-3 px-4">
        <div className="flex gap-2">
          <button 
            onClick={() => onView(booking)} 
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
          >
            <Eye size={12} /> View
          </button>
          {showActions && booking.status === 'Pending' && !booking.accepted_by && (
            <>
              <button
                onClick={() => !isBlocked && onAccept(booking)}
                disabled={isBlocked}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition ${
                  isBlocked 
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
                    : 'text-white bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                <CheckCircle size={12} /> Accept
              </button>
              <button
                onClick={() => onCancel(booking.reqId || booking.bId)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition"
              >
                <XCircle size={12} /> Cancel
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function AdminBooking() {
  const dispatch = useDispatch()
  const { authKey } = useSelector((state) => state.auth)

  const { bookingRequests, loading: requestsLoading, error: requestsError } = useSelector((s) => s.adminBookingRequests)
  const { nearest, ranked, loading: findingNearest, error: findError } = useSelector((s) => s.adminFindNearestDriver)
  const { loading: accepting, success: acceptSuccess, error: acceptError } = useSelector((s) => s.adminAcceptBooking)

  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [acceptModal, setAcceptModal] = useState(null)
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [viewMode, setViewMode] = useState('table')
  const [activeTab, setActiveTab] = useState('unplanned')
  const [acceptedReqIds, setAcceptedReqIds] = useState(new Set())

  // Unplanned bookings state
  const [unplannedBookings, setUnplannedBookings] = useState([])
  const [loadingUnplanned, setLoadingUnplanned] = useState(false)

  // Filters for unplanned
  const [unplannedReqId, setUnplannedReqId] = useState('')
  const [unplannedVehicleId, setUnplannedVehicleId] = useState('')
  const [unplannedDriverNo, setUnplannedDriverNo] = useState('')
  const [unplannedStatus, setUnplannedStatus] = useState('')
  const [unplannedDate, setUnplannedDate] = useState('')
  const [unplannedTonnage, setUnplannedTonnage] = useState('')

  // Filters for scheduled
  const [scheduledReqId, setScheduledReqId] = useState('')
  const [scheduledVehicleId, setScheduledVehicleId] = useState('')
  const [scheduledDriverNo, setScheduledDriverNo] = useState('')
  const [scheduledStatus, setScheduledStatus] = useState('')
  const [scheduledDate, setScheduledDate] = useState('')
  const [scheduledTonnage, setScheduledTonnage] = useState('')

  const [unplannedFiltered, setUnplannedFiltered] = useState([])
  const [scheduledFiltered, setScheduledFiltered] = useState([])
  const [unplannedCurrentPage, setUnplannedCurrentPage] = useState(1)
  const [scheduledCurrentPage, setScheduledCurrentPage] = useState(1)
  const rowsPerPage = 10

  // WebSocket
  const wsRef = useRef(null)
  const lastWsFetchRef = useRef(0)
  const wsFetchTimerRef = useRef(null)
  const BOOKING_WS_URL = 'wss://aeroviewbackend.dockyardsoftware.com:8095/Booking'

  // Detect screen size for default view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewMode('card')
      } else {
        setViewMode('table')
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Fetch scheduled bookings
  const fetchScheduledBookings = useCallback(async () => {
    try {
      await dispatch(FetchBookingRequests())
    } catch (_) { }
  }, [dispatch])

  // Fetch unplanned bookings
  const fetchUnplannedBookings = useCallback(async () => {
    if (!authKey) return
    setLoadingUnplanned(true)
    try {
      const response = await fetch(
        `https://aeroviewbackend.dockyardsoftware.com/BookingRegister/GetMainForkBookingDetails`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "auth-key": authKey,
          },
        }
      )
      const data = await response.json()
      if (data.statusCode === 200 && data.resultSet) {
        const realTimeBookings = data.resultSet
          .filter(booking => booking.BookingStatus === "Real-time Booking")
          .map(booking => ({
            ...booking,
            reqId: booking.bId,
          }))
        setUnplannedBookings(realTimeBookings)
      } else {
        setUnplannedBookings([])
      }
    } catch (error) {
      console.error("Error fetching unplanned bookings:", error)
      setUnplannedBookings([])
    } finally {
      setLoadingUnplanned(false)
    }
  }, [authKey])

  const fetchAllBookings = useCallback(async () => {
    await Promise.all([fetchUnplannedBookings(), fetchScheduledBookings()])
  }, [fetchUnplannedBookings, fetchScheduledBookings])

  useEffect(() => {
    if (authKey) fetchAllBookings()
  }, [authKey, fetchAllBookings])

  // WebSocket connection
  useEffect(() => {
    if (!authKey) return
    if (wsRef.current) wsRef.current.close()

    wsRef.current = new WebSocket(`${BOOKING_WS_URL}?token=${encodeURIComponent(authKey)}`)
    wsRef.current.onopen = () => console.log('✅ Booking WebSocket Connected')
    wsRef.current.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data)
        if (message.type || message.action || message.bId || message.bookingId) {
          const now = Date.now()
          const minGap = 5000
          if (now - lastWsFetchRef.current >= minGap) {
            lastWsFetchRef.current = now
            fetchAllBookings()
          } else if (!wsFetchTimerRef.current) {
            const delay = minGap - (now - lastWsFetchRef.current)
            wsFetchTimerRef.current = setTimeout(() => {
              lastWsFetchRef.current = Date.now()
              wsFetchTimerRef.current = null
              fetchAllBookings()
            }, delay)
          }
        }
      } catch (_) { }
    }
    wsRef.current.onerror = (e) => console.error('WebSocket Error:', e)
    wsRef.current.onclose = () => console.log('WebSocket Disconnected')

    return () => {
      wsRef.current?.close()
      wsRef.current = null
      if (wsFetchTimerRef.current) {
        clearTimeout(wsFetchTimerRef.current)
        wsFetchTimerRef.current = null
      }
    }
  }, [authKey, fetchAllBookings])

  const normalizeDate = (dt) => {
    if (!dt) return ''
    const d = new Date(dt)
    return isNaN(d) ? '' : d.toISOString().split('T')[0]
  }

  const isScheduled = (b) => {
    const bookingStatus = (b.BookingStatus || '').toLowerCase()
    return bookingStatus.includes('shedule') || bookingStatus.includes('schedule') || bookingStatus === 's'
  }

  // Filter unplanned bookings
  useEffect(() => {
    let result = Array.isArray(unplannedBookings) ? [...unplannedBookings] : []
    if (unplannedReqId.trim()) result = result.filter(b => String(b.reqId || b.bId || '').includes(unplannedReqId.trim()))
    if (unplannedVehicleId.trim()) result = result.filter(b => (b.vId || '').toLowerCase().includes(unplannedVehicleId.trim().toLowerCase()))
    if (unplannedDriverNo.trim()) result = result.filter(b => (b.dSNo || '').toLowerCase().includes(unplannedDriverNo.trim().toLowerCase()))
    if (unplannedTonnage.trim()) result = result.filter(b => (b.tonnage || '').toLowerCase().includes(unplannedTonnage.trim().toLowerCase()))
    if (unplannedStatus) result = result.filter(b => (b.status || '') === unplannedStatus)
    if (unplannedDate) result = result.filter(b => normalizeDate(b.bookingDT) === unplannedDate)
    setUnplannedFiltered(result)
    setUnplannedCurrentPage(1)
  }, [unplannedReqId, unplannedVehicleId, unplannedDriverNo, unplannedTonnage, unplannedStatus, unplannedDate, unplannedBookings])

  // Filter scheduled bookings
  useEffect(() => {
    let result = Array.isArray(bookingRequests) ? bookingRequests.filter(b => isScheduled(b)) : []
    if (scheduledReqId.trim()) result = result.filter(b => String(b.reqId || b.bId || '').includes(scheduledReqId.trim()))
    if (scheduledVehicleId.trim()) result = result.filter(b => (b.vId || '').toLowerCase().includes(scheduledVehicleId.trim().toLowerCase()))
    if (scheduledDriverNo.trim()) result = result.filter(b => (b.dSNo || '').toLowerCase().includes(scheduledDriverNo.trim().toLowerCase()))
    if (scheduledTonnage.trim()) result = result.filter(b => (b.tonnage || '').toLowerCase().includes(scheduledTonnage.trim().toLowerCase()))
    if (scheduledStatus) result = result.filter(b => (b.status || '') === scheduledStatus)
    if (scheduledDate) result = result.filter(b => normalizeDate(b.bookingDT) === scheduledDate)
    result.sort((a, b) => new Date(b.bookingDT) - new Date(a.bookingDT))
    setScheduledFiltered(result)
    setScheduledCurrentPage(1)
  }, [scheduledReqId, scheduledVehicleId, scheduledDriverNo, scheduledTonnage, scheduledStatus, scheduledDate, bookingRequests])

  // Conflict detection
  const unplannedConflictIds = useMemo(() => {
    const pending = unplannedBookings.filter(b => b.status === 'Pending')
    return getConflictingReqIds(pending)
  }, [unplannedBookings])

  const scheduledConflictIds = useMemo(() => {
    const scheduled = Array.isArray(bookingRequests) ? bookingRequests.filter(b => isScheduled(b) && b.status === 'Pending') : []
    return getConflictingReqIds(scheduled)
  }, [bookingRequests])

  useEffect(() => {
    if (nearest) setSelectedVehicle(nearest)
  }, [nearest])

  const handleOpenAcceptModal = async (booking) => {
    dispatch(ResetAdminBookingRequest())
    setSelectedVehicle(null)
    setAcceptModal(booking)
    try {
      await dispatch(AdminFindNearestDriver(booking))
    } catch (_) { }
  }

  const handleCancelBooking = async (reqId) => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) return
    try {
      await dispatch(AdminCancelBooking(reqId))
      await fetchAllBookings()
    } catch (err) {
      console.error(err)
      alert("❌ Cancel failed")
    }
  }

  const validateRequestInfo = async (reqId, authKey) => {
    try {
      const res = await fetch(
        `https://aeroviewbackend.dockyardsoftware.com/BookingRegister/Getrequestinfo?rid=${reqId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "auth-key": authKey,
          },
        }
      )
      const data = await res.json()
      return data
    } catch (err) {
      console.error("Validation API error:", err)
      return false
    }
  }

  const handleConfirmAccept = async () => {
    if (!acceptModal || !selectedVehicle) return
    try {
      await dispatch(AdminAcceptBooking(acceptModal, selectedVehicle, authKey))
      const reqId = acceptModal.reqId || acceptModal.bId
      const isValid = await validateRequestInfo(reqId, authKey)
      if (!isValid) {
        alert("❌ Request validation failed.")
        return
      }
      setAcceptedReqIds(prev => new Set([...prev, String(reqId)]))
      await fetchAllBookings()
      setAcceptModal(null)
      dispatch(ResetAdminBookingRequest())
    } catch (error) {
      console.error(error)
      alert("❌ Something went wrong")
    }
  }

  const handleCloseAcceptModal = () => {
    setAcceptModal(null)
    setSelectedVehicle(null)
    dispatch(ResetAdminBookingRequest())
  }

  const handleClearUnplannedFilters = () => {
    setUnplannedReqId('')
    setUnplannedVehicleId('')
    setUnplannedDriverNo('')
    setUnplannedTonnage('')
    setUnplannedStatus('')
    setUnplannedDate('')
  }

  const handleClearScheduledFilters = () => {
    setScheduledReqId('')
    setScheduledVehicleId('')
    setScheduledDriverNo('')
    setScheduledTonnage('')
    setScheduledStatus('')
    setScheduledDate('')
  }

  // Stats
  const totalCount = unplannedBookings.length + (Array.isArray(bookingRequests) ? bookingRequests.filter(b => isScheduled(b)).length : 0)
  const pendingCount = [...unplannedBookings, ...(Array.isArray(bookingRequests) ? bookingRequests.filter(b => isScheduled(b)) : [])].filter(b => b.status === 'Pending').length
  const acceptedCount = [...unplannedBookings, ...(Array.isArray(bookingRequests) ? bookingRequests.filter(b => isScheduled(b)) : [])].filter(b => b.status === 'Active' || b.status === 'Accepted').length
  const scheduleCount = Array.isArray(bookingRequests) ? bookingRequests.filter(b => isScheduled(b)).length : 0
  const unplannedCount = unplannedBookings.length

  const statusColor = (s) => {
    switch (s) {
      case 'Active': return 'bg-emerald-50 text-emerald-700'
      case 'Pending': return 'bg-yellow-50 text-yellow-700'
      case 'Completed': return 'bg-blue-50 text-blue-700'
      case 'Enroute': return 'bg-purple-50 text-purple-700'
      case 'In Progress': return 'bg-indigo-50 text-indigo-700'
      case 'Cancel': return 'bg-red-50 text-red-700'
      default: return 'bg-gray-50 text-gray-700'
    }
  }

  const statusOptions = ['Pending', 'Active', 'Completed', 'Enroute', 'In Progress', 'Cancel']

  // Pagination helpers
  const getCurrentPageData = (data, page) => {
    const indexOfLast = page * rowsPerPage
    const indexOfFirst = indexOfLast - rowsPerPage
    return data.slice(indexOfFirst, indexOfLast)
  }

  const totalPages = (data) => Math.max(1, Math.ceil(data.length / rowsPerPage))

  const isLoading = loadingUnplanned || requestsLoading

  return (
    <AdminLayout>
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-xl">
                <ClipboardList size={22} className="text-blue-600" />
              </div>
              Booking Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage and track all booking requests with real-time updates
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full border border-emerald-200">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
              <span className="text-xs font-medium text-emerald-700">Live Updates Active</span>
            </div>
            <button 
              onClick={fetchAllBookings} 
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
        <StatCard title="Total Requests" value={totalCount} icon={ClipboardList} color="blue" />
        <StatCard title="Unplanned" value={unplannedCount} icon={Zap} color="purple" />
        <StatCard title="Scheduled" value={scheduleCount} icon={CalendarDays} color="indigo" />
        <StatCard title="Pending" value={pendingCount} icon={Clock} color="yellow" />
        <StatCard title="Accepted" value={acceptedCount} icon={CheckCircle} color="green" />
      </div>

      {/* Conflict Legend */}
      <div className="flex items-center gap-6 mb-5 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
          <span className="text-gray-600">Conflict — Overlapping time slot</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-400"></div>
          <span className="text-gray-600">Blocked — Another overlapping booking accepted</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
          <span className="text-gray-600">Live — Real-time updates</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        <TabButton
          active={activeTab === 'unplanned'}
          onClick={() => setActiveTab('unplanned')}
          icon={Zap}
          count={unplannedFiltered.length}
        >
          Unplanned Bookings
        </TabButton>
        <TabButton
          active={activeTab === 'scheduled'}
          onClick={() => setActiveTab('scheduled')}
          icon={CalendarDays}
          count={scheduledFiltered.length}
        >
          Scheduled Bookings
        </TabButton>
      </div>

      {/* Unplanned Section */}
      {activeTab === 'unplanned' && (
        <>
          {/* Search and Filters */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
              <FilterInput 
                placeholder="Request ID" 
                value={unplannedReqId} 
                onChange={setUnplannedReqId} 
                icon={Search}
              />
              <FilterInput 
                placeholder="Vehicle ID" 
                value={unplannedVehicleId} 
                onChange={setUnplannedVehicleId} 
                icon={Truck}
              />
              <FilterInput 
                placeholder="Driver No" 
                value={unplannedDriverNo} 
                onChange={setUnplannedDriverNo} 
                icon={User}
              />
              <FilterInput 
                placeholder="Tonnage" 
                value={unplannedTonnage} 
                onChange={setUnplannedTonnage} 
                icon={Weight}
              />
              <FilterSelect 
                value={unplannedStatus} 
                onChange={setUnplannedStatus} 
                options={statusOptions}
              />
              <FilterDate 
                value={unplannedDate} 
                onChange={setUnplannedDate}
              />
            </div>
            
            {(unplannedReqId || unplannedVehicleId || unplannedDriverNo || unplannedTonnage || unplannedStatus || unplannedDate) && (
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Showing <span className="font-semibold text-gray-700">{unplannedFiltered.length}</span> of {unplannedCount} unplanned requests
                </p>
                <button
                  onClick={handleClearUnplannedFilters}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                >
                  <X size={12} />
                  Clear all filters
                </button>
              </div>
            )}
          </div>

          {/* View Toggle */}
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">
              {unplannedFiltered.length} booking{unplannedFiltered.length !== 1 ? 's' : ''} found
            </p>
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  viewMode === 'table' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Table size={14} className="inline mr-1.5" />
                Table View
              </button>
              <button
                onClick={() => setViewMode('card')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  viewMode === 'card' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Grid size={14} className="inline mr-1.5" />
                Card View
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw size={20} className="text-gray-400 animate-spin" />
                <span className="ml-3 text-sm text-gray-500">Loading unplanned bookings...</span>
              </div>
            ) : requestsError ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 text-sm rounded-lg">
                  <AlertCircle size={16} />
                  {requestsError}
                </div>
              </div>
            ) : unplannedFiltered.length === 0 ? (
              <div className="text-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <div className="p-3 bg-gray-100 rounded-full">
                    <ClipboardList size={32} className="text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-500">No unplanned booking requests found</p>
                </div>
              </div>
            ) : (
              <>
                {/* Table View */}
                <div className={`${viewMode === 'table' ? 'block' : 'hidden'} overflow-x-auto`}>
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Req ID</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Booked By</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tonnage</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Route</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Time Slot</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {getCurrentPageData(unplannedFiltered, unplannedCurrentPage).map((b) => (
                        <TableRow
                          key={b.reqId || b.bId}
                          booking={b}
                          onView={setSelectedBooking}
                          onAccept={handleOpenAcceptModal}
                          onCancel={handleCancelBooking}
                          statusColor={statusColor}
                          normalizeDate={normalizeDate}
                          conflictIds={unplannedConflictIds}
                          acceptedReqIds={acceptedReqIds}
                          showActions={false}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Card View */}
                <div className={`${viewMode === 'card' ? 'block' : 'hidden'} p-4 space-y-3`}>
                  {getCurrentPageData(unplannedFiltered, unplannedCurrentPage).map((b) => {
                    const hasConflict = unplannedConflictIds.has(String(b.reqId || b.bId))
                    const isBlocked = hasConflict && acceptedReqIds.size > 0 && !acceptedReqIds.has(String(b.reqId || b.bId))
                    return (
                      <BookingCard
                        key={b.reqId || b.bId}
                        booking={b}
                        onView={setSelectedBooking}
                        onAccept={handleOpenAcceptModal}
                        onCancel={handleCancelBooking}
                        statusColor={statusColor}
                        normalizeDate={normalizeDate}
                        hasConflict={hasConflict}
                        isBlocked={isBlocked}
                        showActions={false}
                      />
                    )
                  })}
                </div>

                {/* Pagination */}
                {unplannedFiltered.length > rowsPerPage && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                    <p className="text-sm text-gray-500">
                      Showing <span className="font-medium text-gray-700">{(unplannedCurrentPage - 1) * rowsPerPage + 1}</span> to <span className="font-medium text-gray-700">{Math.min(unplannedCurrentPage * rowsPerPage, unplannedFiltered.length)}</span> of <span className="font-medium text-gray-700">{unplannedFiltered.length}</span> results
                    </p>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setUnplannedCurrentPage(prev => Math.max(1, prev - 1))} 
                        disabled={unplannedCurrentPage === 1} 
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        <ChevronLeft size={14} />
                        Previous
                      </button>
                      <span className="px-3 py-1.5 text-sm text-gray-600">
                        Page {unplannedCurrentPage} of {totalPages(unplannedFiltered)}
                      </span>
                      <button 
                        onClick={() => setUnplannedCurrentPage(prev => Math.min(totalPages(unplannedFiltered), prev + 1))} 
                        disabled={unplannedCurrentPage === totalPages(unplannedFiltered)} 
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        Next
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Scheduled Section */}
      {activeTab === 'scheduled' && (
        <>
          {/* Search and Filters */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
              <FilterInput 
                placeholder="Request ID" 
                value={scheduledReqId} 
                onChange={setScheduledReqId} 
                icon={Search}
              />
              <FilterInput 
                placeholder="Vehicle ID" 
                value={scheduledVehicleId} 
                onChange={setScheduledVehicleId} 
                icon={Truck}
              />
              <FilterInput 
                placeholder="Driver No" 
                value={scheduledDriverNo} 
                onChange={setScheduledDriverNo} 
                icon={User}
              />
              <FilterInput 
                placeholder="Tonnage" 
                value={scheduledTonnage} 
                onChange={setScheduledTonnage} 
                icon={Weight}
              />
              <FilterSelect 
                value={scheduledStatus} 
                onChange={setScheduledStatus} 
                options={statusOptions}
              />
              <FilterDate 
                value={scheduledDate} 
                onChange={setScheduledDate}
              />
            </div>
            
            {(scheduledReqId || scheduledVehicleId || scheduledDriverNo || scheduledTonnage || scheduledStatus || scheduledDate) && (
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Showing <span className="font-semibold text-gray-700">{scheduledFiltered.length}</span> of {scheduleCount} scheduled requests
                </p>
                <button
                  onClick={handleClearScheduledFilters}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                >
                  <X size={12} />
                  Clear all filters
                </button>
              </div>
            )}
          </div>

          {/* View Toggle */}
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">
              {scheduledFiltered.length} booking{scheduledFiltered.length !== 1 ? 's' : ''} found
            </p>
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  viewMode === 'table' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Table size={14} className="inline mr-1.5" />
                Table View
              </button>
              <button
                onClick={() => setViewMode('card')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  viewMode === 'card' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Grid size={14} className="inline mr-1.5" />
                Card View
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw size={20} className="text-gray-400 animate-spin" />
                <span className="ml-3 text-sm text-gray-500">Loading scheduled bookings...</span>
              </div>
            ) : requestsError ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 text-sm rounded-lg">
                  <AlertCircle size={16} />
                  {requestsError}
                </div>
              </div>
            ) : scheduledFiltered.length === 0 ? (
              <div className="text-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <div className="p-3 bg-gray-100 rounded-full">
                    <ClipboardList size={32} className="text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-500">No scheduled booking requests found</p>
                </div>
              </div>
            ) : (
              <>
                {/* Table View */}
                <div className={`${viewMode === 'table' ? 'block' : 'hidden'} overflow-x-auto`}>
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Req ID</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Booked By</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tonnage</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Route</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Time Slot</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {getCurrentPageData(scheduledFiltered, scheduledCurrentPage).map((b) => (
                        <TableRow
                          key={b.reqId || b.bId}
                          booking={b}
                          onView={setSelectedBooking}
                          onAccept={handleOpenAcceptModal}
                          onCancel={handleCancelBooking}
                          statusColor={statusColor}
                          normalizeDate={normalizeDate}
                          conflictIds={scheduledConflictIds}
                          acceptedReqIds={acceptedReqIds}
                          showActions={true}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Card View */}
                <div className={`${viewMode === 'card' ? 'block' : 'hidden'} p-4 space-y-3`}>
                  {getCurrentPageData(scheduledFiltered, scheduledCurrentPage).map((b) => {
                    const hasConflict = scheduledConflictIds.has(String(b.reqId || b.bId))
                    const isBlocked = hasConflict && acceptedReqIds.size > 0 && !acceptedReqIds.has(String(b.reqId || b.bId))
                    return (
                      <BookingCard
                        key={b.reqId || b.bId}
                        booking={b}
                        onView={setSelectedBooking}
                        onAccept={handleOpenAcceptModal}
                        onCancel={handleCancelBooking}
                        statusColor={statusColor}
                        normalizeDate={normalizeDate}
                        hasConflict={hasConflict}
                        isBlocked={isBlocked}
                        showActions={true}
                      />
                    )
                  })}
                </div>

                {/* Pagination */}
                {scheduledFiltered.length > rowsPerPage && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                    <p className="text-sm text-gray-500">
                      Showing <span className="font-medium text-gray-700">{(scheduledCurrentPage - 1) * rowsPerPage + 1}</span> to <span className="font-medium text-gray-700">{Math.min(scheduledCurrentPage * rowsPerPage, scheduledFiltered.length)}</span> of <span className="font-medium text-gray-700">{scheduledFiltered.length}</span> results
                    </p>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setScheduledCurrentPage(prev => Math.max(1, prev - 1))} 
                        disabled={scheduledCurrentPage === 1} 
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        <ChevronLeft size={14} />
                        Previous
                      </button>
                      <span className="px-3 py-1.5 text-sm text-gray-600">
                        Page {scheduledCurrentPage} of {totalPages(scheduledFiltered)}
                      </span>
                      <button 
                        onClick={() => setScheduledCurrentPage(prev => Math.min(totalPages(scheduledFiltered), prev + 1))} 
                        disabled={scheduledCurrentPage === totalPages(scheduledFiltered)} 
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        Next
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full border border-gray-100 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <ClipboardList size={18} className="text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Booking Details</h2>
              </div>
              <button 
                onClick={() => setSelectedBooking(null)} 
                className="p-2 hover:bg-gray-100 rounded-xl transition"
              >
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <InfoField label="Request ID" value={selectedBooking.reqId || selectedBooking.bId} />
                <InfoField label="Status" value={selectedBooking.status} badge={statusColor(selectedBooking.status)} />
                <InfoField label="Booked By" value={selectedBooking.bookingSno} />
                <InfoField label="Tonnage" value={`${selectedBooking.tonnage} tons`} />
                <InfoField label="Vehicle ID" value={selectedBooking.vId || '-'} />
                <InfoField label="Driver No" value={selectedBooking.dSNo || '-'} />
                <InfoField label="Booking Type" value={selectedBooking.BookingStatus || '-'} />
                <InfoField label="Booking Date" value={normalizeDate(selectedBooking.bookingDT)} />
                <InfoField label="From" value={selectedBooking.From || '-'} />
                <InfoField label="To" value={selectedBooking.TO || '-'} />
                <InfoField label="Start Location" value={`${selectedBooking.slatitude || '-'}, ${selectedBooking.slongitude || '-'}`} />
                <InfoField label="End Location" value={`${selectedBooking.elatitude || '-'}, ${selectedBooking.elongitude || '-'}`} />
                <InfoField label="Scheduled Start" value={selectedBooking.bsTime ? new Date(selectedBooking.bsTime).toLocaleString() : '-'} />
                <InfoField label="Scheduled End" value={selectedBooking.beTime ? new Date(selectedBooking.beTime).toLocaleString() : '-'} />
              </div>
              <div className="mt-3">
                <InfoField label="Remark" value={selectedBooking.Remark || '-'} />
              </div>
            </div>
            
            <div className="flex gap-3 p-5 border-t border-gray-100 sticky bottom-0 bg-white">
              {selectedBooking.status === 'Pending' && !selectedBooking.accepted_by && (
                <button 
                  onClick={() => { setSelectedBooking(null); handleOpenAcceptModal(selectedBooking); }} 
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition"
                >
                  <CheckCircle size={16} />
                  Accept & Assign Driver
                </button>
              )}
              <button 
                onClick={() => setSelectedBooking(null)} 
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Accept & Assign Modal */}
      {acceptModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full border border-gray-100 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-xl">
                  <CheckCircle size={18} className="text-emerald-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Accept & Assign Booking</h2>
              </div>
              <button onClick={handleCloseAcceptModal} className="p-2 hover:bg-gray-100 rounded-xl transition">
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            
            <div className="p-5">
              {/* Booking Summary */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-5">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <ClipboardList size={14} />
                  Booking Summary
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Request ID:</span>
                    <p className="font-semibold text-gray-900 mt-0.5">{acceptModal.reqId || acceptModal.bId}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Tonnage:</span>
                    <p className="font-semibold text-orange-600 mt-0.5">{acceptModal.tonnage} tons</p>
                  </div>
                  <div>
                    <span className="text-gray-500">From:</span>
                    <p className="font-semibold text-gray-900 mt-0.5">{acceptModal.From || '—'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">To:</span>
                    <p className="font-semibold text-gray-900 mt-0.5">{acceptModal.TO || '—'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Booked By:</span>
                    <p className="font-semibold text-gray-900 mt-0.5">{acceptModal.bookingSno}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Scheduled Start:</span>
                    <p className="font-semibold text-gray-900 mt-0.5">{acceptModal.bsTime ? new Date(acceptModal.bsTime).toLocaleString() : '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Destination Lat:</span>
                    <p className="font-semibold text-gray-900 mt-0.5">{acceptModal.elatitude || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Destination Lon:</span>
                    <p className="font-semibold text-gray-900 mt-0.5">{acceptModal.elongitude || '-'}</p>
                  </div>
                </div>
              </div>
              
              {/* Vehicle Selection */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Truck size={14} />
                  Select Vehicle to Assign
                </h3>
                
                {findingNearest && (
                  <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl">
                    <RefreshCw size={18} className="text-blue-600 animate-spin" />
                    <span className="text-sm text-blue-700">Finding matching vehicles...</span>
                  </div>
                )}
                
                {findError && !findingNearest && (
                  <div className="flex items-start gap-3 p-4 bg-red-50 rounded-xl">
                    <AlertCircle size={18} className="text-red-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-red-700">No Matching Vehicle Found</p>
                      <p className="text-xs text-red-600 mt-1">{findError}</p>
                    </div>
                  </div>
                )}
                
                {ranked.length > 0 && !findingNearest && (
                  <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                    {ranked.map((v, idx) => {
                      const isSelected = selectedVehicle?.vehicleId === v.vehicleId
                      const isNearest = idx === 0
                      return (
                        <div 
                          key={v.vehicleId} 
                          onClick={() => setSelectedVehicle(v)} 
                          className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${
                            isSelected 
                              ? 'border-emerald-500 bg-emerald-50' 
                              : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              {isSelected ? (
                                <CheckCircle size={18} className="text-emerald-600" />
                              ) : (
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                              )}
                              <span className="text-base font-semibold text-gray-900">{v.vehicleId}</span>
                              {isNearest && (
                                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">
                                  Nearest
                                </span>
                              )}
                            </div>
                            <span className="text-sm font-semibold text-blue-600">{v.distanceKm} km</span>
                          </div>
                          <div className="grid grid-cols-3 gap-3 text-sm text-gray-600 ml-7">
                            <div>
                              <span className="text-gray-400">Driver:</span>
                              <span className="ml-1 font-medium">{v.driverSno}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Weight:</span>
                              <span className="ml-1 font-medium text-orange-600">{v.weightCat}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Type:</span>
                              <span className="ml-1 font-medium">{v.vehicleType}</span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
              
              {acceptError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl">
                  <p className="text-sm text-red-600 flex items-center gap-2">
                    <AlertCircle size={14} />
                    {acceptError}
                  </p>
                </div>
              )}
              
              {acceptSuccess && (
                <div className="mt-4 p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                  <p className="text-sm text-emerald-600 flex items-center gap-2">
                    <CheckCircle size={14} />
                    Booking accepted — request sent to driver successfully.
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 p-5 border-t border-gray-100 sticky bottom-0 bg-white">
              <button 
                onClick={handleCloseAcceptModal} 
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition"
              >
                Cancel
              </button>
              <button 
                onClick={handleConfirmAccept} 
                disabled={!selectedVehicle || findingNearest || accepting} 
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {accepting ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Sending to Driver...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Accept & Assign {selectedVehicle ? `(${selectedVehicle.vehicleId})` : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}